import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import '../styles/whatsapp.css';

/**
 * Per-category kill switches for outbound WhatsApp messages.
 *
 * Separate from an employee's own `whatsappNotificationsEnabled` preference
 * (that one opts a person out of every WhatsApp message; these opt the whole
 * company out of one *kind* of message). The category list itself — labels,
 * descriptions, which ones already have a real sender wired up — comes from
 * the server (server/utils/whatsappNotificationCategories.js) rather than
 * being duplicated here, so adding a category never means touching two files
 * that have to agree.
 *
 * Toggling is optimistic: the switch flips immediately and only reverts if
 * the save actually fails, since a round trip before the switch moves reads
 * as a broken toggle on the always-present WhatsApp browser-session lag.
 */
const WhatsAppNotificationSettings = () => {
    const [categories, setCategories] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/whatsapp/notification-settings');
            setCategories(res.data.categories);
        } catch (err) {
            setCategories([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = async (key, nextEnabled) => {
        const previous = categories;
        setCategories((current) => current.map((c) => (c.key === key ? { ...c, enabled: nextEnabled } : c)));
        setSavingKey(key);
        try {
            const res = await api.put('/whatsapp/notification-settings', { [key]: nextEnabled });
            setCategories(res.data.categories);
        } catch (err) {
            setCategories(previous);
            Swal.fire('Could not save', err.response?.data?.message || 'That change did not save. Try again.', 'error');
        } finally {
            setSavingKey('');
        }
    };

    if (loading) {
        return (
            <div className="control-card wa-card">
                <p className="wa-loading"><FontAwesomeIcon icon={faSpinner} spin /> Loading notification settings…</p>
            </div>
        );
    }

    const active = (categories || []).filter((c) => c.status === 'active');
    const planned = (categories || []).filter((c) => c.status === 'planned');

    const row = (c) => (
        <div className="wa-notif-row" key={c.key}>
            <div className="wa-notif-info">
                <p className="wa-notif-label">{c.label}</p>
                <p className="wa-notif-desc">{c.description}</p>
            </div>
            <label className="wa-toggle" title={c.enabled ? 'On — sending' : 'Off — this message type will not be sent'}>
                <input
                    type="checkbox"
                    checked={Boolean(c.enabled)}
                    disabled={savingKey === c.key}
                    onChange={(e) => toggle(c.key, e.target.checked)}
                />
                <span className="wa-toggle-track" />
            </label>
        </div>
    );

    return (
        <div className="control-card wa-card">
            <div className="wa-head">
                <h3 className="wa-title">
                    <FontAwesomeIcon icon={faSliders} /> Notification Settings
                </h3>
            </div>
            <p className="wa-hint" style={{ marginTop: '-6px' }}>
                Turn individual WhatsApp notification types on or off company-wide. In-app notifications
                are never affected — this only controls what additionally goes out over WhatsApp.
            </p>

            {!categories?.length ? (
                <p className="wa-hint">Could not load notification settings. Try refreshing the page.</p>
            ) : (
                <>
                    <div className="wa-notif-group">
                        <h4 className="wa-notif-group-title">Currently sent</h4>
                        {active.map(row)}
                    </div>

                    {planned.length > 0 && (
                        <div className="wa-notif-group">
                            <h4 className="wa-notif-group-title">Planned</h4>
                            <p className="wa-notif-group-hint">
                                Not built yet — these switches are ready for when they are, so nothing changes
                                by turning one on today.
                            </p>
                            {planned.map(row)}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default WhatsAppNotificationSettings;
